import type { LandingHero } from 'js/analytics_v2/types';

export type AnalyticsPreview = 'premium' | 'non_premium';
export type AnalyticsAccessModeSelection = AnalyticsPreview | 'actual';

export type AnalyticsAccessContext = {
  viewer_id: number;
  viewed_user_id: number;
  experience: 'new' | 'legacy';
  access_mode: AnalyticsPreview;
  admin_preview: boolean;
  analytics_preview: AnalyticsPreview | null;
  fixtures: boolean;
};

export const analyticsPreviewFromUrl = (
  href?: string,
): AnalyticsPreview | undefined => {
  if (!href && typeof window === 'undefined') return undefined;
  const params = new URL(href ?? window.location.href).searchParams;
  const preview = params.get('analytics_preview');
  if (preview === 'premium' || preview === 'non_premium') return preview;
  return params.get('non_premium') === 'true' ? 'non_premium' : undefined;
};

export const withAnalyticsPreview = (
  endpoint: string,
  preview: AnalyticsPreview | undefined,
): string => {
  if (!preview) return endpoint;
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.delete('non_premium');
  url.searchParams.set('analytics_preview', preview);
  return endpoint.startsWith('/') ? `${url.pathname}${url.search}` : url.href;
};

export const withAnalyticsLandingContext = (
  endpoint: string,
  context?: AnalyticsAccessContext,
): string => {
  if (!context?.admin_preview) return endpoint;
  const url = new URL(endpoint, window.location.origin);
  if (
    url.pathname !== '/upgrade' ||
    url.searchParams.get('feature') !== 'analytics_dashboard'
  )
    return endpoint;
  url.searchParams.set('viewed_user_id', String(context.viewed_user_id));
  const destination = endpoint.startsWith('/')
    ? `${url.pathname}${url.search}`
    : url.href;
  return withAnalyticsPreview(destination, 'non_premium');
};

export type AnalyticsV2Options = {
  fixtures: boolean;
  delay: boolean;
  nonPremium?: boolean;
  periodPicker?: boolean;
  landingVariant?: LandingHero['landing_variant'];
};

export const ANALYTICS_V2_RELOAD_EVENT = 'analytics-v2:reload';
export const ANALYTICS_V2_READERS_TOUR_EVENT = 'analytics-v2:readers-tour';
export const ANALYTICS_V2_FEEDBACK_EVENT = 'analytics-v2:feedback';
export const ANALYTICS_V2_STATUS_EVENT = 'analytics-v2:status';
export const ANALYTICS_V2_STATUS_REQUEST_EVENT = 'analytics-v2:status-request';

export type AnalyticsStatus = {
  context?: AnalyticsAccessContext;
  checking: boolean;
  options: AnalyticsV2Options;
  readersTourAvailable: boolean;
  feedbackAvailable?: boolean;
};
